/*
  M365 desktop menu. Loaded only when "Enable M365 desktop menu" is on.

  Works alongside Dawn's HeaderMenu (details-disclosure.js) and the summary handlers in global.js,
  which keep handling click, focus-out and Esc while focus is inside a panel. This adds:
  - Hover mode: opens after a short intent delay, closes when the pointer leaves the item and its panel,
    and swaps panels instantly (no open animation) when moving between top items.
  - One panel open at a time, in both modes.
  - Esc and outside click also close a panel that was opened by hover (focus isn't inside it).
  - Closes on scroll down when the sticky header is set to hide on scroll.
*/
(function () {
  const OPEN_DELAY = 150;
  const CLOSE_DELAY = 250;

  function initMegaMenu(nav) {
    if (nav.dataset.m365MegaReady) return;
    nav.dataset.m365MegaReady = 'true';

    const hover = nav.dataset.m365Open === 'hover';
    const items = Array.from(nav.querySelectorAll(':scope > .list-menu--inline > li')).filter((li) =>
      li.querySelector(':scope > header-menu > details')
    );
    const getDetails = (li) => li.querySelector(':scope > header-menu > details');
    const getOpen = () => items.map(getDetails).filter((details) => details.open);
    let openTimer;
    let closeTimer;

    const setExpanded = (details) => {
      details.querySelector('summary').setAttribute('aria-expanded', details.open);
    };

    const close = (details) => {
      if (!details.open) return;
      details.removeAttribute('open');
      setExpanded(details);
    };

    const open = (details, swap) => {
      // Close first so the header's preventHide ends up reflecting the panel that stays open.
      getOpen().forEach((other) => other !== details && close(other));
      nav.classList.toggle('m365-mega--swap', Boolean(swap));
      if (!details.open) details.setAttribute('open', '');
      setExpanded(details);
    };

    const clearTimers = () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };

    items.forEach((li) => {
      const details = getDetails(li);

      details.addEventListener('toggle', () => {
        if (details.open) {
          getOpen().forEach((other) => other !== details && close(other));
        } else {
          details.removeAttribute('data-m365-hover');
        }
        setExpanded(details);
        if (!getOpen().length) nav.classList.remove('m365-mega--swap');

        // Dawn's HeaderMenu sets preventHide per toggle, so a late close event can clear it while
        // another panel is open. Recompute from the actual state.
        const header = document.querySelector('.header-wrapper');
        if (header) header.preventHide = getOpen().length > 0;
      });

      if (!hover) return;

      li.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimers();
        if (details.open) return;

        const current = getOpen();
        if (current.length) {
          details.setAttribute('data-m365-hover', '');
          open(details, true);
          return;
        }

        openTimer = setTimeout(() => {
          details.setAttribute('data-m365-hover', '');
          open(details, false);
        }, OPEN_DELAY);
      });

      li.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(openTimer);
        if (!details.open || !details.hasAttribute('data-m365-hover')) return;
        closeTimer = setTimeout(() => close(details), CLOSE_DELAY);
      });

      // A click on a panel that hover already opened keeps it open (and pinned until Esc, outside
      // click or another click) instead of toggling it shut under the pointer.
      details.querySelector('summary').addEventListener('click', (event) => {
        clearTimers();
        if (details.open && details.hasAttribute('data-m365-hover')) {
          event.preventDefault();
          details.removeAttribute('data-m365-hover');
          setExpanded(details);
        }
      });
    });

    if (hover) {
      // Dawn replays the first open animation it cached on every open, which would fade in a panel
      // during a swap. Let the CSS rule run the animation instead, so the swap class can skip it.
      customElements.whenDefined('header-menu').then(() => {
        items.forEach((li) => {
          li.querySelector(':scope > header-menu').animations = [];
        });
      });
    }

    document.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape' && event.key !== 'Escape') return;
      getOpen().forEach((details) => {
        const hadFocus = details.contains(document.activeElement);
        close(details);
        if (hadFocus) details.querySelector('summary').focus();
      });
    });

    document.addEventListener('click', (event) => {
      if (nav.contains(event.target)) return;
      getOpen().forEach(close);
    });

    const sticky = nav.closest('sticky-header');
    if (sticky && sticky.dataset.stickyType === 'on-scroll-up') {
      let lastScroll = window.scrollY;
      window.addEventListener(
        'scroll',
        () => {
          const scroll = window.scrollY;
          if (scroll > lastScroll && scroll > sticky.offsetHeight) {
            getOpen().forEach(close);
          }
          lastScroll = scroll;
        },
        { passive: true }
      );
    }
  }

  function initAll() {
    document.querySelectorAll('nav.m365-mega').forEach(initMegaMenu);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
